import React, { ReactNode } from 'react';
import AirplayIcon from 'components/svg/16px-icon-livestream.svg';
import LocationOnIcon from 'components/svg/16px-icon-location.svg';
import CalendarIcon from 'components/svg/icon-calendar.svg';
import ClockIcon from 'components/svg/icon-clock-outline.svg';
import { Box, Divider, Grid, Typography, Zoom } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { formatDuration, formatPriceStart, formatTimeDuration } from 'utils/format';
import { routes } from 'utils/routing';
import { CountOfCategories } from 'utils/constants';
import { EventCategory } from 'api/admin/models';
import CheckIcon from 'components/svg/CheckIcon1.svg';

import LocationIcon from 'components/svg/location.svg';
import VirtualIcon from 'components/svg/virtual.svg';
import Image from 'components/common/Image';
import Label from 'components/common/Label';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { getSessionsGroups, isSessionEndTimeHidden, MARKETS_SLUGS_MAP } from '../../../utils/transform';
import { DetectMarketSelectors } from '../../../store/selectors';
import LinkNoPropagate from '../LinkNoPropagate';
import useCustomOrgShowTime from '../../../hooks/useCustomOrgShowTime';
import TooltipInfo from '../../admin/TooltipInfo';
import OrgsBox from './OrgsBox';

interface EventCardProps {
  id?: number;
  children?: ReactNode;
  imageLabel?: ReactNode;
  fab?: ReactNode;
  heart?: ReactNode;
  imagePopUp?: ReactNode;
  title: string;
  organizationId?: number;
  organizationTitle: string;
  organizationLogo: string;
  organization_mentions?: any[];
  settings?: any;
  startAt?: string;
  endAt?: string;
  timeZone?: string;
  imageUrl?: string;
  categories?: EventCategory[];
  targetAges?: any;
  currency?: string;
  minTicketPrice?: number;
  maxTicketPrice?: number;
  hasLocation?: boolean;
  isTest?: boolean;
  isVirtual?: boolean;
  isPopUpToggled?: boolean;
  eventRoute?: any;
  organizationRoute?: any;
  hideDate?: boolean;
  hideTime?: boolean;
  isHiddenPrice?: boolean;
  noLocationLiveIcons?: boolean;
  padding?: number;
  sessions?: any;
  userIsRegistered?: boolean;
}

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isTestEnv = () => BUILD_ENV === 'test';
const isProductionEnv = () => BUILD_ENV === 'production';

const useStyles = makeStyles()((theme) => ({
  root: {
    height: `calc(100% + ${theme.spacing(3)})`,
    cursor: 'pointer',
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    transform: 'translateY(0) rotateX(0) scale(1)',
    transformOrigin: 'center center',
    opacity: 1,
    transition: 'transform 0.6s cubic-bezier(.1, .3, .2, 1), opacity 0.6s cubic-bezier(.1, .3, .2, 1)',
    '&:hover': {
      transform: 'translateY(0) rotateX(0) scale(1.04)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  imageContainer: {
    position: 'relative',
  },
  imageRegisteredContainer: {
    position: 'absolute',
    width: '100%',
    height: 28,
    borderRadius: '0 0 8px 8px',
    backgroundColor: '#3753D7',
    color: 'white',
    bottom: theme.spacing(0),
    left: theme.spacing(0),
  },
  imageLabelContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
  },
  fabContainer: {
    display: 'flex',
    position: 'absolute',
    alignItems: 'center',
    height: 0,
    top: 0,
    right: theme.spacing(2),
    zIndex: 2,
  },
  imagePopUpContainer: {
    position: 'absolute',
    zIndex: 2,
    width: '100%',
    height: '100%',
  },
  dateTypo: {
    fontWeight: 500,
    lineHeight: 1,
  },
  organizationTypo: {
    fontWeight: 500,
    textDecoration: 'unset',
  },
  organizationMentionsTypo: {
    fontWeight: 500,
  },
  orgHover: {
    '&:hover': {
      color: '#3449A9',
    },
  },
  link: {},
  wordBreak: {
    display: '-webkit-box',
    WebkitLineClamp: 3, // кількість рядків
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    textDecoration: 'none',
  },
  titleContainer: {
    // marginTop: 0,
  },
  orgWrapper: {
    backgroundColor: 'white',
    width: 'fit-content',
    padding: '28px 8px 8px 12px',
  },
  orgTitleWrapper: {
    paddingTop: '0px !important',
    paddingLeft: '16px !important',
  },
  orgImageContainer: {
    position: 'relative',
    minWidth: 48,
    minHeight: 48,
    paddingTop: '0px !important',
    paddingLeft: '0px !important',
    zIndex: 100,
    '.secondary &': {
      [theme.breakpoints.down('md')]: {
        width: 104,
        overflow: 'hidden',
        '& img': {
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
        },
      },
    },
  },
}));

const EventCard: React.FC<React.PropsWithChildren<EventCardProps>> = (props) => {
  const {
    id,
    children,
    imageLabel,
    fab,
    heart,
    imagePopUp,
    isPopUpToggled,
    title,
    organizationTitle,
    organizationLogo,
    organization_mentions = [],
    isTest,
    startAt,
    endAt,
    timeZone,
    imageUrl,
    categories,
    targetAges,
    currency,
    minTicketPrice,
    maxTicketPrice,
    hasLocation,
    isVirtual,
    settings,
    eventRoute,
    organizationRoute,
    hideDate,
    hideTime,
    isHiddenPrice,
    noLocationLiveIcons,
    padding,
    sessions,
    organizationId,
    userIsRegistered,
  } = props;
  const [t] = useTranslation();
  const { classes } = useStyles();

  const sliceOfCategories = categories.slice(0, CountOfCategories);
  const router = useRouter();
  const queryOrganizationId = Number(router.query.organizationId);
  const { customShowTime } = useCustomOrgShowTime(organizationId || queryOrganizationId);

  const hideEndTime = isSessionEndTimeHidden(sessions, endAt);
  const time = startAt && endAt && formatTimeDuration(startAt, hideEndTime ? null : endAt, timeZone);
  const timesLabel = () => {
    if (Array.isArray(sessions) && sessions.length > 1) {
      return getSessionsGroups(sessions, timeZone).length > 1
        ? t('common:general.multipleDays')
        : t('common:general.multipleTimes');
    }
    return (
      <>
        {time.split('|')[0]} <span>{time.split('|')[1]}</span>
      </>
    );
  };

  const market = useSelector(DetectMarketSelectors.getSelectedMarket);

  const isPriceTag = () => {
    if (isHiddenPrice) return '';
    if (id === 6047 && isProductionEnv() && !minTicketPrice && !maxTicketPrice) return '';
    if (id === 1953 && isTestEnv() && !minTicketPrice && !maxTicketPrice) return '';
    return true;
  };

  return (
    <Grid
      id={`wtf${id}`}
      onClick={() => router.push(eventRoute.href)}
      className={classes.root}
      container
      spacing={3}
      alignContent="space-between"
      style={{ paddingTop: 0, paddingLeft: 0 }}
    >
      <Grid item xs={12} style={{ paddingTop: 0, paddingLeft: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className={classes.imageContainer}>
              <Zoom in={isPopUpToggled}>
                <div className={classes.imagePopUpContainer}>{imagePopUp}</div>
              </Zoom>
              <Image
                variant="rectangle"
                rounded
                src={imageUrl}
                alt={title}
                priority
                placeholder={isVirtual && !hasLocation ? <VirtualIcon /> : <LocationIcon />}
              />
              {userIsRegistered && (
                <div className={classes.imageRegisteredContainer}>
                  <Grid container spacing={0.5} justifyContent="center" alignItems="center" style={{ height: '100%' }}>
                    <Grid item>
                      <CheckIcon />
                    </Grid>
                    <Grid item>
                      <Typography textAlign="center" variant="body2" fontWeight={500}>
                        {t('common:general.registered')}
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
              )}
              {fab && <div className={classes.fabContainer}>{fab}</div>}
              {imageLabel && <div className={classes.imageLabelContainer}>{imageLabel}</div>}
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              paddingTop: 24,
              paddingLeft: 36,
              paddingRight: 20,
              paddingBottom: 0,
              marginBottom: 0,
              ...(padding && { paddingLeft: padding * 2, paddingRight: padding }),
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {startAt && timeZone && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                  {!hideDate && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon />
                      <Typography className={classes.dateTypo} color="textSecondary" variant="body2">
                        {formatDuration(startAt, endAt, timeZone)}
                      </Typography>
                    </Box>
                  )}
                  {!hideTime && customShowTime && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ClockIcon />
                      <Typography className={classes.dateTypo} color="textSecondary" variant="body2">
                        {timesLabel()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {heart && <Box sx={{ flexShrink: 0, ml: 1 }}>{heart}</Box>}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            className={classes.titleContainer}
            sx={{ pt: '16px' }}
            style={{
              paddingLeft: 36,
              paddingRight: 20,
              ...(padding && { paddingLeft: padding * 2, paddingRight: padding }),
            }}
          >
            <Typography className={classes.wordBreak} color="textPrimary" variant="h6" sx={{ lineHeight: 1 }}>
              {title}
            </Typography>
            <LinkNoPropagate href={organizationRoute.link} underline="none">
              <Typography className={classes.organizationTypo} color="textSecondary" variant="body2" component="span">
                <Grid className={classes.orgWrapper} container spacing={1.5}>
                  <Grid item className={classes.orgImageContainer}>
                    <Image
                      variant="square"
                      objectFit="cover"
                      src={organizationLogo || '/static/img/defaultOrgLogo.png'}
                      alt={organizationTitle}
                      priority
                    />
                  </Grid>
                  <Grid item xs className={classes.orgTitleWrapper}>
                    <Typography color="textSecondary" variant="body2" style={{ fontWeight: 500 }} component="div">
                      {t<string>('common:general.hostedBy')}
                    </Typography>
                    <Typography color="primary" variant="body2" style={{ fontWeight: 500 }} component="span">
                      <OrgsBox
                        eventId={id}
                        orgs={[
                          { id: organizationId, title: organizationTitle },
                          ...organization_mentions.map((org) => ({
                            id: org.organization.id,
                            title: org.organization.title,
                          })),
                        ]}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </Typography>
            </LinkNoPropagate>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          paddingLeft: 20,
          paddingRight: 20,
          paddingBottom: 32,
          paddingTop: 16,
          ...(padding && {
            paddingLeft: padding * 2.5,
            paddingRight: padding,
            paddingBottom: 12,
          }),
          // paddingTop: 0,
          // paddingLeft: 0,
        }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs container spacing={1} alignItems="center">
            {(() => {
              const MAX_LABELS = 8;

              // 1️⃣ Build arrays
              const categoryLabels = sliceOfCategories.map((category) => {
                const route = routes.events({
                  category: category.id,
                  market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                  ordering: 'session_date',
                });
                return (
                  <Grid item key={`category-${category.id}`}>
                    <LinkNoPropagate href={route.link} underline="none">
                      <Label color="lightGray" textColor="darkGray" size="small" label={category.title} hasHover />
                    </LinkNoPropagate>
                  </Grid>
                );
              });

              const targetAgeLabels = targetAges.map((targetAge: any) => {
                const route = routes.events({
                  target_age: targetAge.id,
                  market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                  ordering: 'session_date',
                });
                return (
                  <Grid item key={`age-${targetAge.id}`}>
                    <LinkNoPropagate href={route.link} underline="none">
                      <Label
                        color="extraLightPurple"
                        textColor="purple"
                        size="small"
                        label={targetAge.title}
                        hasHover
                      />
                    </LinkNoPropagate>
                  </Grid>
                );
              });

              const extraLabels: JSX.Element[] = [];

              if (isPriceTag()) {
                const route = routes.events({
                  price: minTicketPrice === 0 ? 'free' : 'paid',
                  market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                  ordering: 'session_date',
                });
                extraLabels.push(
                  <Grid item key="price">
                    <LinkNoPropagate href={route.link} underline="none">
                      <Label
                        color="green"
                        textColor="green"
                        variant="outlined"
                        size="small"
                        label={formatPriceStart(minTicketPrice || 0, maxTicketPrice || 0, currency, true, true)}
                        hasHover
                      />
                    </LinkNoPropagate>
                  </Grid>,
                );
              }

              if (settings && !settings.is_public) {
                extraLabels.push(
                  <Grid item key="private">
                    <Label
                      variant="outlined"
                      color="secondary"
                      textColor="secondary"
                      size="small"
                      label={t<string>('common:general.privatUrl')}
                    />
                  </Grid>,
                );
              }

              if (isTest) {
                extraLabels.push(
                  <Grid item key="test">
                    <Label
                      variant="outlined"
                      color="secondary"
                      textColor="secondary"
                      size="small"
                      label={t<string>('common:general.testEvent')}
                    />
                  </Grid>,
                );
              }

              // 2️⃣ Logic for filling priority slots
              const reservedSlotsForExtras = extraLabels.length;
              const availableSlotsBeforeExtras = Math.max(0, MAX_LABELS - reservedSlotsForExtras);

              // Fill categories first
              const limitedCategories = categoryLabels.slice(0, availableSlotsBeforeExtras);

              // Fill remaining space between categories and extras with target ages
              const remainingSlotsAfterCategories = availableSlotsBeforeExtras - limitedCategories.length;
              const limitedTargetAges = targetAgeLabels.slice(0, remainingSlotsAfterCategories);

              // 3️⃣ Combine in correct display order
              const allLabels = [...limitedCategories, ...limitedTargetAges, ...extraLabels].slice(0, MAX_LABELS);

              return allLabels;
            })()}
          </Grid>

          {!noLocationLiveIcons && (hasLocation || isVirtual) && (
            <Grid item>
              <Grid container spacing={1}>
                <Grid item lineHeight={0}>
                  <Grid container spacing={0.5}>
                    {hasLocation && (
                      <Grid item>
                        <TooltipInfo
                          icon={LocationOnIcon}
                          textNode="Offline event"
                          width={118}
                          padding="16px"
                          varinat="caption"
                        />
                      </Grid>
                    )}
                    {isVirtual && (
                      <Grid item>
                        <TooltipInfo
                          icon={AirplayIcon}
                          textNode="Online event"
                          width={118}
                          padding="16px"
                          varinat="caption"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          {children && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                {children}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EventCard;
